import { ChevronRight, Info } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'shadcn/components/ui/tooltip';
import { cn } from 'shadcn/lib/utils';

/**
 * A form select component with a label, tooltip, and error message.
 * @param {object} props
 * @param {string} props.label - The label for the select.
 * @param {boolean} props.required - Whether the select is required.
 * @param {string} props.tooltip - The tooltip for the select.
 * @param {string} props.error - The error message for the select.
 * @param {object[]} props.options - The options for the select.
 * @param {string} props.options[].value - The value of the option.
 * @param {string} props.options[].label - The label of the option.
 * @param {boolean} props.options[].disabled - Whether the option is disabled.
 * @param {object} props.props - The props for the select.
 * @param {boolean} props.disabled - Whether the select is disabled.
 * @returns {JSX.Element}
 */
function FormSelect({ disabled, error, label, options, required, tooltip, ...props }) {
    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-600 flex items-center">
                    {label}
                    {required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                {tooltip && (
                    <TooltipProvider>
                        <Tooltip modal>
                            <TooltipTrigger asChild>
                                <Info className="h-3 w-3 text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent className="z-[10000]">
                                <p>{tooltip}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
            </div>
            <div className="relative">
                <select
                    {...props}
                    disabled={disabled}
                    className={cn(
                        'block appearance-none w-full border-0 border-b rounded-none px-1 h-auto py-1.5 bg-transparent text-sm',
                        'focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-blue-500',
                        error ? 'border-red-500' : 'border-gray-300',
                        disabled && 'bg-gray-100 cursor-not-allowed text-gray-500',
                    )}
                >
                    {options.map((option) => (
                        <option key={option.value} value={option.value} disabled={option.disabled}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <ChevronRight className="h-4 w-4" />
                </div>
            </div>
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </div>
    );
}

export default FormSelect;
